// One flat as an architect's plan: zones, walls, windows, doors with their
// swing, standard furniture, room names and areas, overall dimensions, a
// scale bar and north.
//
// The drawing is turned so the flat's longest wall runs across the page —
// a plan read at an angle is a plan nobody reads — and the north arrow turns
// with it. Units are metres throughout; the SVG's viewBox is in metres, so a
// printed copy can be scaled off.
import { area, grow, type MultiPoly, type Pt } from "../geom.ts";
import type { Furnished, Furniture } from "../furniture.ts";
import type { RoomKind, Unit } from "../layout.ts";
import { entryRoom, facadeEdges } from "../openings.ts";
import { hallAllowance, PASS_THROUGH } from "../rooms.ts";

export type Zone = "zi" | "noapte" | "umed" | "circulatie" | "depozitare";

export const ZONE: Record<RoomKind, Zone> = { living: "zi", kitchen: "zi", bedroom: "noapte", bath: "umed", hall: "circulatie", storage: "depozitare" };
export const ZONE_NAME: Record<Zone, string> = { zi: "Zona de zi", noapte: "Zona de noapte", umed: "Grupuri sanitare", circulatie: "Circulație", depozitare: "Depozitare" };
export const ZONE_FILL: Record<Zone, string> = { zi: "#fbe3c4", noapte: "#d8e6f8", umed: "#c9ebe8", circulatie: "#ebebeb", depozitare: "#e4ddd0" };

/** Room names as a plan writes them: bedrooms numbered, a living room with no kitchen of its own says so. */
export function roomNames(u: Unit): string[] {
  const rooms = u.rooms ?? [];
  const kitchen = rooms.some((r) => r.kind === "kitchen");
  let bed = 0;
  const beds = rooms.filter((r) => r.kind === "bedroom").length;
  const livings = rooms.filter((r) => r.kind === "living").length;
  let liv = 0;
  return rooms.map((r) => {
    switch (r.kind) {
      case "living": return `${kitchen || liv > 0 ? "Cameră de zi" : "Cameră de zi + bucătărie"}${livings > 1 ? ` ${++liv}` : ""}`;
      case "bedroom": return beds > 1 ? `Dormitor ${++bed}` : "Dormitor";
      case "kitchen": return "Bucătărie";
      case "bath": return "Baie";
      case "hall": return "Hol";
      case "storage": return "Debara";
    }
  });
}

const f2 = (n: number) => n.toFixed(2).replace(".", ",");
const f1 = (n: number) => n.toFixed(1).replace(".", ",");
const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;");

export interface PlanOptions {
  /** Draw the furniture (default true). */
  furniture?: boolean;
  /** Draw the dimension lines (default true). */
  dimensions?: boolean;
  /** For the editor: rooms carry data-room and are clickable; this one is highlighted. */
  interactive?: boolean;
  selected?: number;
}

/**
 * How a flat is laid on the page: turned so its longest outline edge runs
 * across, in metres. `P` takes a plan point to the page, `inv` back, and
 * `ux` / `uy` are the plan directions of the page's x and of its up.
 */
export function planFrame(flat: Unit) {
  const ring = flat.polygon[0]?.[0] ?? [];
  let theta = 0, longest = 0;
  ring.forEach((a, i) => { const b = ring[(i + 1) % ring.length]; const L = Math.hypot(b[0] - a[0], b[1] - a[1]); if (L > longest) { longest = L; theta = Math.atan2(b[1] - a[1], b[0] - a[0]); } });
  // keep text upright: never turn more than a quarter either way
  if (theta > Math.PI / 2) theta -= Math.PI; else if (theta < -Math.PI / 2) theta += Math.PI;
  const c = Math.cos(-theta), s = Math.sin(-theta);
  const rot = (p: Pt): Pt => [p[0] * c - p[1] * s, p[0] * s + p[1] * c];
  const unrot = (p: Pt): Pt => [p[0] * c + p[1] * s, -p[0] * s + p[1] * c];
  const all = [...flat.polygon, ...(flat.balcony ?? [])].flatMap((p) => p[0].map(rot));
  const minX = Math.min(...all.map((p) => p[0])), maxX = Math.max(...all.map((p) => p[0]));
  const minY = Math.min(...all.map((p) => p[1])), maxY = Math.max(...all.map((p) => p[1]));
  const pad = 1.6;
  const W = maxX - minX + 2 * pad, H = maxY - minY + 2 * pad + 0.9; // room under the plan for the scale bar
  // plan (x, y up) to page (x, y down), and back
  const P = (p: Pt): Pt => { const q = rot(p); return [q[0] - minX + pad, maxY - q[1] + pad]; };
  const inv = (q: Pt): Pt => unrot([q[0] + minX - pad, maxY + pad - q[1]]);
  const V = (v: Pt): Pt => { const q = rot(v); return [q[0], -q[1]]; };
  return { P, inv, V, rot, W, H, minX, maxX, minY, maxY, pad, ux: unrot([1, 0]), uy: unrot([0, 1]) };
}

export function flatPlanSvg(flat: Unit, storey: MultiPoly, furnished: Furnished, opt: PlanOptions = {}): string {
  // zero-width spikes left by the booleans draw as stray walls: an opening (shrink, then grow) removes them
  const clean = (mp: MultiPoly) => { const c = grow(grow(mp, -0.01), 0.01); return c.length ? c : mp; };
  const u: Unit = { ...flat, polygon: clean(flat.polygon), rooms: flat.rooms?.map((r) => ({ ...r, polygon: clean(r.polygon) })) };
  const rooms = u.rooms ?? [];
  const names = roomNames(u);
  const { P, V, W, H, minX, maxX, minY, maxY, pad } = planFrame(u);
  const path = (mp: MultiPoly) => mp.map((poly) => poly.map((r) => `M${r.map((p) => P(p).map((x) => x.toFixed(3)).join(" ")).join("L")}Z`).join("")).join("");
  const line = (a: Pt, b: Pt, attrs: string) => { const [x1, y1] = P(a), [x2, y2] = P(b); return `<line x1="${x1.toFixed(3)}" y1="${y1.toFixed(3)}" x2="${x2.toFixed(3)}" y2="${y2.toFixed(3)}" ${attrs}/>`; };

  const out: string[] = [];
  out.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W.toFixed(2)} ${H.toFixed(2)}" width="${(W * 60).toFixed(0)}" height="${(H * 60).toFixed(0)}" font-family="system-ui, -apple-system, Segoe UI, sans-serif">`);
  out.push(`<defs><pattern id="hatch" width="0.25" height="0.25" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="0.25" stroke="#9ca3af" stroke-width="0.02"/></pattern></defs>`);
  out.push(`<rect width="100%" height="100%" fill="#ffffff"/>`);

  // balcony, then zones
  if (u.balcony) out.push(`<path d="${path(u.balcony)}" fill="url(#hatch)" stroke="#6b7280" stroke-width="0.05"/>`);
  rooms.forEach((r, i) => out.push(`<path d="${path(r.polygon)}" fill="${ZONE_FILL[ZONE[r.kind]]}" fill-rule="evenodd"${opt.interactive ? ` data-room="${i}" style="cursor:pointer"` : ""}/>`));

  // furniture, under the walls so a piece against a wall tucks into it
  if (opt.furniture !== false) for (const f of furnished.pieces) out.push(piece(f, P, V));

  // walls: every room edge as a partition, the flat's outline heavier, the facade heaviest
  rooms.forEach((r) => out.push(`<path d="${path(r.polygon)}" fill="none" stroke="#374151" stroke-width="0.1" stroke-linejoin="miter"/>`));
  out.push(`<path d="${path(u.polygon)}" fill="none" stroke="#111827" stroke-width="0.2" stroke-linejoin="miter"/>`);
  for (const [a, b] of facadeEdges(u.polygon, storey)) out.push(line(a, b, `stroke="#111827" stroke-width="0.3" stroke-linecap="square"`));

  if (opt.selected != null && rooms[opt.selected]) out.push(`<path d="${path(rooms[opt.selected].polygon)}" fill="#2f5bea" fill-opacity="0.12" stroke="#2f5bea" stroke-width="0.12" pointer-events="none"/>`);
  // openings: cut the wall, then draw the symbol
  for (const o of u.openings ?? []) {
    const L = Math.hypot(o.b[0] - o.a[0], o.b[1] - o.a[1]);
    if (L < 0.2) continue;
    out.push(line(o.a, o.b, `stroke="#ffffff" stroke-width="0.34"`));
    const t: Pt = [(o.b[0] - o.a[0]) / L, (o.b[1] - o.a[1]) / L];
    const n: Pt = [-t[1], t[0]];
    const off = (p: Pt, k: number): Pt => [p[0] + n[0] * k, p[1] + n[1] * k];
    if (o.kind === "window" || o.kind === "balconyDoor" || o.kind === "shopfront") {
      for (const k of [-0.12, 0.12]) out.push(line(off(o.a, k), off(o.b, k), `stroke="#111827" stroke-width="0.03"`));
      out.push(line(o.a, o.b, `stroke="#2563eb" stroke-width="0.05"`));
      if (o.kind === "balconyDoor") out.push(line(off(o.a, 0.04), off(o.b, 0.04), `stroke="#2563eb" stroke-width="0.02"`));
      continue;
    }
    if (o.kind !== "door" && o.kind !== "frontDoor") continue;
    // the leaf swings into the room being entered: the second of the pair, or the flat for its front door
    const into = o.kind === "frontDoor" ? u.polygon : o.joins && o.joins[1] >= 0 ? rooms[o.joins[1]]?.polygon ?? u.polygon : u.polygon;
    const mid: Pt = [(o.a[0] + o.b[0]) / 2, (o.a[1] + o.b[1]) / 2];
    const side = insideAny(off(mid, 0.3), into) ? 1 : -1;
    const hinge = o.a, tip: Pt = off(o.a, side * L);
    out.push(line(hinge, tip, `stroke="#111827" stroke-width="${o.kind === "frontDoor" ? 0.06 : 0.04}"`));
    const [bx, by] = P(o.b), [tx, ty] = P(tip);
    // sweep direction follows the turn from the closed leaf (along t) to the open one (along n)
    const cross = t[0] * (side * n[1]) - t[1] * (side * n[0]);
    out.push(`<path d="M${bx.toFixed(3)} ${by.toFixed(3)}A${L.toFixed(3)} ${L.toFixed(3)} 0 0 ${cross > 0 ? 0 : 1} ${tx.toFixed(3)} ${ty.toFixed(3)}" fill="none" stroke="#6b7280" stroke-width="0.02" stroke-dasharray="0.08 0.05"/>`);
  }
  if (u.door) { const [x, y] = P(u.door); out.push(`<path d="M${x} ${y}m-0.18 0l0.18 -0.18l0.18 0.18z" fill="#16a34a"/>`); }

  // room names and areas, with a halo so furniture under them stays legible
  rooms.forEach((r, i) => {
    const [x, y] = labelPoint(r.polygon, P);
    const a = area(r.polygon);
    const small = a < 4;
    out.push(`<text x="${x.toFixed(2)}" y="${y.toFixed(2)}" text-anchor="middle" font-size="${small ? 0.22 : 0.3}" fill="#111827" stroke="#ffffff" stroke-width="0.08" paint-order="stroke" font-weight="600">${esc(names[i])}</text>`);
    out.push(`<text x="${x.toFixed(2)}" y="${(y + (small ? 0.28 : 0.38)).toFixed(2)}" text-anchor="middle" font-size="${small ? 0.2 : 0.26}" fill="#374151" stroke="#ffffff" stroke-width="0.08" paint-order="stroke">${f2(a)} m²</text>`);
  });
  if (u.balcony) { const [x, y] = labelPoint(u.balcony, P); out.push(`<text x="${x.toFixed(2)}" y="${(y + 0.1).toFixed(2)}" text-anchor="middle" font-size="0.22" fill="#374151" stroke="#ffffff" stroke-width="0.06" paint-order="stroke">Balcon ${f2(area(u.balcony))} m²</text>`); }

  // overall dimensions, outside the plan
  if (opt.dimensions !== false) {
    const x0 = pad, x1 = maxX - minX + pad, y0 = pad, y1 = maxY - minY + pad;
    const dim = (ax: number, ay: number, bx: number, by: number, text: string, vertical: boolean) => {
      const tick = (x: number, y: number) => `<line x1="${x - 0.1}" y1="${y + 0.1}" x2="${x + 0.1}" y2="${y - 0.1}" stroke="#374151" stroke-width="0.025"/>`;
      const mx = (ax + bx) / 2, my = (ay + by) / 2;
      return `<line x1="${ax}" y1="${ay}" x2="${bx}" y2="${by}" stroke="#374151" stroke-width="0.02"/>${tick(ax, ay)}${tick(bx, by)}` +
        `<text x="${vertical ? mx - 0.12 : mx}" y="${vertical ? my : my - 0.1}" font-size="0.24" fill="#374151" text-anchor="middle" ${vertical ? `transform="rotate(-90 ${mx - 0.12} ${my})"` : ""}>${text}</text>`;
    };
    out.push(dim(x0, y0 - 0.7, x1, y0 - 0.7, `${f2(x1 - x0)} m`, false));
    out.push(dim(x0 - 0.7, y0, x0 - 0.7, y1, `${f2(y1 - y0)} m`, true));
  }

  // scale bar and north
  const sy = H - 0.55, sx = pad;
  out.push([0, 1, 2, 3, 4].map((k) => `<rect x="${sx + k}" y="${sy}" width="1" height="0.12" fill="${k % 2 ? "#ffffff" : "#111827"}" stroke="#111827" stroke-width="0.02"/>`).join(""));
  out.push(`<text x="${sx}" y="${sy + 0.38}" font-size="0.22" fill="#374151">0</text><text x="${sx + 5}" y="${sy + 0.38}" font-size="0.22" fill="#374151" text-anchor="middle">5 m</text>`);
  const [nx, ny] = V([0, 1]);
  const ax = W - pad + 0.6, ay = H - 1.0;
  out.push(`<g transform="translate(${ax} ${ay}) rotate(${(Math.atan2(nx, -ny) * 180) / Math.PI})"><path d="M0 -0.45L0.18 0.3L0 0.15L-0.18 0.3Z" fill="#111827"/><text y="-0.55" font-size="0.26" text-anchor="middle" fill="#111827" font-weight="700">N</text></g>`);
  out.push(`</svg>`);
  return out.join("\n");
}

function insideAny(p: Pt, mp: MultiPoly): boolean {
  for (const poly of mp) {
    let inside = false;
    poly.forEach((ring, k) => {
      let c = false;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i], [xj, yj] = ring[j];
        if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) c = !c;
      }
      if (k === 0) inside = c; else if (c) inside = false;
    });
    if (inside) return true;
  }
  return false;
}

/** A point well inside a room for its label: the area centroid, or the middle of its widest scan line if that falls outside. */
function labelPoint(mp: MultiPoly, P: (p: Pt) => Pt): Pt {
  let cx = 0, cy = 0, a = 0;
  for (const poly of mp) {
    const r = poly[0];
    for (let i = 0; i < r.length; i++) {
      const p = r[i], q = r[(i + 1) % r.length];
      const k = p[0] * q[1] - q[0] * p[1];
      a += k; cx += (p[0] + q[0]) * k; cy += (p[1] + q[1]) * k;
    }
  }
  if (Math.abs(a) > 1e-9) { const c: Pt = [cx / (3 * a), cy / (3 * a)]; if (insideAny(c, mp)) { const q = P(c); return [q[0], q[1] - 0.1]; } }
  const pts = mp.flatMap((p) => p[0]);
  const q = P(pts.reduce((best, p) => (insideAny([p[0] * 0.9 + pts[0][0] * 0.1, p[1] * 0.9 + pts[0][1] * 0.1], mp) ? p : best), pts[0]));
  return q;
}

/** One piece of furniture, drawn in its own frame: x along the wall, y into the room. */
function piece(f: Furniture, P: (p: Pt) => Pt, V: (v: Pt) => Pt): string {
  const [ox, oy] = P(f.o), [ux, uy] = V(f.u), [nx, ny] = V(f.n);
  const { w, d } = f;
  const st = `fill="#ffffff" stroke="#475569" stroke-width="0.025"`;
  const thin = `fill="none" stroke="#94a3b8" stroke-width="0.018"`;
  const R = (x: number, y: number, ww: number, hh: number, extra = st, rx = 0) => `<rect x="${x}" y="${y}" width="${ww}" height="${hh}" rx="${rx}" ${extra}/>`;
  let g = "";
  switch (f.kind) {
    case "bedDouble":
    case "bedSingle": {
      g = R(0, 0, w, d);
      const pillows = f.kind === "bedDouble" ? 2 : 1;
      for (let k = 0; k < pillows; k++) g += R(0.08 + k * (w - 0.16) / pillows, 0.08, (w - 0.16) / pillows - 0.06, 0.32, st, 0.06);
      g += `<path d="M0 0.6H${w}" ${thin}/><path d="M0 0.6L${w * 0.25} 0.85L${w} 0.6" ${thin}/>`;
      break;
    }
    case "wardrobe": g = R(0, 0, w, d) + `<path d="M0 0L${w} ${d}M${w} 0L0 ${d}" ${thin}/>`; break;
    case "desk": g = R(0, 0, w, d) + `<circle cx="${w / 2}" cy="${d + 0.3}" r="0.22" ${st}/>`; break;
    case "sofa":
      g = R(0, 0, w, d, st, 0.08) + R(0, 0, w, 0.22, st) + R(0, 0, 0.2, d, st) + R(w - 0.2, 0, 0.2, d, st) +
        `<path d="M${w / 2} 0.22V${d}" ${thin}/>` + R(w / 2 - 0.5, d + 0.4, 1.0, 0.55, st, 0.05);
      break;
    case "diningTable": {
      g = R(0, 0, w, d);
      const n = Math.max(1, Math.floor(w / 0.6));
      for (let k = 0; k < n; k++) { const x = (k + 0.5) * (w / n) - 0.21; g += R(x, -0.46, 0.42, 0.4, st, 0.05) + R(x, d + 0.06, 0.42, 0.4, st, 0.05); }
      if (w >= 1.4) g += R(-0.46, d / 2 - 0.21, 0.4, 0.42, st, 0.05) + R(w + 0.06, d / 2 - 0.21, 0.4, 0.42, st, 0.05);
      break;
    }
    case "counter": {
      g = R(0, 0, w, d);
      // a fridge at the end of a full-length run
      if (w >= 2.4) g += R(0, 0, 0.6, d, `fill="#f1f5f9" stroke="#475569" stroke-width="0.025"`) + `<path d="M0.05 ${d - 0.08}H0.55" ${thin}/>`;
      const sinkAt = Math.min(w - 1.4, w >= 2.4 ? 0.9 : 0.15);
      g += R(Math.max(0.1, sinkAt), 0.1, 0.5, 0.4, st, 0.06);
      const hob = w - 0.7;
      for (const [x, y] of [[0.15, 0.15], [0.45, 0.15], [0.15, 0.42], [0.45, 0.42]]) g += `<circle cx="${hob + x}" cy="${y}" r="0.1" ${thin}/>`;
      break;
    }
    case "bathtub": g = R(0, 0, w, d) + R(0.07, 0.07, w - 0.14, d - 0.14, st, 0.25) + `<circle cx="${w - 0.25}" cy="${d / 2}" r="0.04" ${thin}/>`; break;
    case "shower": g = R(0, 0, w, d) + `<path d="M0 0L${w} ${d}M${w} 0L0 ${d}" ${thin}/><circle cx="${w / 2}" cy="${d / 2}" r="0.05" ${st}/>`; break;
    case "wc": g = R(0, 0, w, 0.18) + `<ellipse cx="${w / 2}" cy="${0.18 + 0.24}" rx="${w / 2 - 0.02}" ry="0.24" ${st}/>`; break;
    case "basin": g = R(0, 0, w, d, st, 0.04) + `<ellipse cx="${w / 2}" cy="${d / 2 + 0.03}" rx="${w / 2 - 0.08}" ry="${d / 2 - 0.08}" ${thin}/>`; break;
    case "washer": g = R(0, 0, w, d) + `<circle cx="${w / 2}" cy="${d / 2}" r="${w / 2 - 0.1}" ${thin}/>`; break;
    case "hallUnit": g = R(0, 0, w, d) + Array.from({ length: Math.floor(w / 0.2) }, (_, k) => `<path d="M${0.1 + k * 0.2} 0.05V${d - 0.05}" ${thin}/>`).join(""); break;
    case "shelves": g = R(0, 0, w, d) + `<path d="M0 ${d / 2}H${w}" ${thin} stroke-dasharray="0.1 0.06"/>`; break;
  }
  return `<g transform="matrix(${ux.toFixed(4)} ${uy.toFixed(4)} ${nx.toFixed(4)} ${ny.toFixed(4)} ${ox.toFixed(3)} ${oy.toFixed(3)})">${g}</g>`;
}

/** The flat in words, for beside the drawing. */
export function flatSummary(u: Unit): { label: string; value: string }[] {
  const rooms = u.rooms ?? [];
  const byZone = new Map<Zone, number>();
  for (const r of rooms) byZone.set(ZONE[r.kind], (byZone.get(ZONE[r.kind]) ?? 0) + area(r.polygon));
  return [
    { label: "Suprafață construită", value: `${f1(u.area)} m²` },
    { label: "Suprafață utilă", value: `${f1(u.usable ?? 0)} m²` },
    ...(u.balcony ? [{ label: "Balcon", value: `${f1(area(u.balcony))} m²` }] : []),
    ...[...byZone].map(([z, a]) => ({ label: ZONE_NAME[z], value: `${f1(a)} m²` })),
    { label: "Soare în camera de zi, 21 dec.", value: `${f1(u.sunHours ?? 0)} h` },
  ];
}

/** The rules of rooms.ts, checked on one flat, in words: what holds and what does not. */
export function roomChecks(u: Unit): { ok: boolean; text: string }[] {
  const rooms = u.rooms ?? [];
  const names = roomNames(u);
  const hall = rooms.filter((r) => r.kind === "hall").reduce((t, r) => t + area(r.polygon), 0);
  const usable = u.usable ?? 0;
  const out: { ok: boolean; text: string }[] = [];
  out.push({ ok: hall <= hallAllowance(usable) + 0.05, text: `Holul: ${f1(hall)} m², ${f1((100 * hall) / (usable || 1))}% din suprafața utilă (maxim 15% sau 6 m²)` });
  if (u.door) {
    const e = rooms[entryRoom(rooms, u.door)];
    const hasHall = rooms.some((r) => r.kind === "hall");
    out.push({ ok: !!e && (e.kind === "hall" || (!hasHall && e.kind === "living")), text: e ? `Intrarea dă în ${names[rooms.indexOf(e)].toLowerCase()}` : "Intrarea nu dă în nicio cameră" });
  }
  const wrong = (u.openings ?? []).filter((o) => o.kind === "door" && o.joins && !PASS_THROUGH[rooms[o.joins[1]].kind].includes(rooms[o.joins[0]].kind));
  out.push({ ok: !wrong.length, text: wrong.length ? `Se trece prin ${wrong.map((o) => `${names[o.joins![0]].toLowerCase()} spre ${names[o.joins![1]].toLowerCase()}`).join(", ")}` : "Nicio cameră nu se intră prin baie, bucătărie sau alt dormitor" });
  return out;
}
